import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import * as guruChatService from 'services/GuruChatService'
import { openWidget, setView } from 'ducks/guruWidget/actions'
import { selectGuruWidgetOpen } from 'ducks/guruWidget/selectors'
import { useGuruContext } from './hooks/useGuruContext'
import { useGuruVisibility } from './hooks/useGuruVisibility'
import { readGuruPreferences } from './guruPreferences'

const DWELL_MS = 3000
const TOAST_MS = 4000

const Toast = styled.div`
  position: fixed;
  right: 24px;
  bottom: 96px;
  max-width: 320px;
  background: #fff;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  z-index: 1250;
  display: flex;
  align-items: center;
  gap: 10px;
  border-left: 3px solid #e53935;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 768px) {
    right: 16px;
    bottom: calc(var(--bp-float-1) + var(--bp-stack-height, 0px));
    z-index: 900;
  }
`

const OpenBtn = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
`

const DismissBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[200]};
  padding: 4px;
  font-size: 16px;
  line-height: 1;
`

const GuruPeek = () => {
  const dispatch = useDispatch()
  const ctx = useGuruContext()
  const visible = useGuruVisibility()
  const widgetOpen = useSelector(selectGuruWidgetOpen)
  const [peek, setPeek] = useState(null)
  const dwellRef = useRef(null)
  const toastRef = useRef(null)
  const firedKeyRef = useRef(null)

  useEffect(() => {
    if (!visible || widgetOpen || !ctx.contextType || !ctx.contextId) return undefined
    if (!readGuruPreferences().showCriticalPeeks) return undefined
    const key = `${ctx.contextType}:${ctx.contextId}`
    if (firedKeyRef.current === key) return undefined

    dwellRef.current = setTimeout(async () => {
      try {
        const elig = await guruChatService.getPeekEligibility({ contextType: ctx.contextType, contextId: ctx.contextId })
        if (!elig.data?.eligible) return
        const recs = await guruChatService.getRecommendations({ contextType: ctx.contextType, contextId: ctx.contextId, surface: 'peek' })
        const critical = (recs.data?.data || []).find(c => c.tier === 'critical')
        if (!critical) return
        firedKeyRef.current = key
        setPeek(critical)
        guruChatService.logPeek({ contextType: ctx.contextType, contextId: ctx.contextId }).catch(() => {})
        toastRef.current = setTimeout(() => setPeek(null), TOAST_MS)
      } catch (e) {
        // Silently swallow — peek failure shouldn't disturb the UI.
      }
    }, DWELL_MS)

    return () => {
      if (dwellRef.current) clearTimeout(dwellRef.current)
      if (toastRef.current) clearTimeout(toastRef.current)
    }
  }, [visible, widgetOpen, ctx.contextType, ctx.contextId])

  if (!peek || widgetOpen) return null

  const handleOpen = () => {
    dispatch(openWidget(ctx))
    dispatch(setView('suggestions'))
    setPeek(null)
  }

  const node = (
    <Toast role="status">
      <span style={{ flex: 1 }}>{peek.title}</span>
      <OpenBtn onClick={handleOpen}>Open</OpenBtn>
      <DismissBtn onClick={() => setPeek(null)} aria-label="Dismiss alert">×</DismissBtn>
    </Toast>
  )

  return ReactDOM.createPortal(node, document.body)
}

export default GuruPeek
